'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabaseBrowserClient } from '@/lib/supabase/client';
import type { AuthProfile } from '@/lib/types';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: AuthProfile | null;
  loading: boolean;
  authError: string | null;
  signIn: (email: string, password: string) => Promise<AuthProfile>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const AUTH_TIMEOUT_MS = 12000;
const PROFILE_CACHE_KEY = 'shopattend-profile-cache-v1';
const INACTIVE_WORKER_MESSAGE = 'Your worker account is inactive. Please contact the admin.';

async function withTimeout<T>(promise: Promise<T>, message: string) {
  return await Promise.race<T>([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error(message)), AUTH_TIMEOUT_MS);
    }),
  ]);
}

async function fetchProfile(userId: string) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();

  if (error) {
    throw error;
  }

  return data as AuthProfile;
}

function readCachedProfile(userId?: string | null) {
  if (typeof window === 'undefined' || !userId) return null;

  try {
    const raw = window.localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { userId: string; profile: AuthProfile };
    return parsed.userId === userId ? parsed.profile : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(profile: AuthProfile) {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ userId: profile.id, profile }));
}

function clearCachedProfile() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(PROFILE_CACHE_KEY);
}

function shouldClearSession(error: unknown) {
  if (!error || typeof error !== 'object') return false;

  const maybeCode = 'code' in error ? String(error.code ?? '') : '';
  const maybeStatus = 'status' in error ? Number(error.status ?? 0) : 0;
  const maybeMessage = 'message' in error ? String(error.message ?? '') : '';

  return (
    maybeCode === 'PGRST116' ||
    maybeStatus === 401 ||
    maybeStatus === 403 ||
    maybeMessage.toLowerCase().includes('jwt') ||
    maybeMessage.toLowerCase().includes('invalid session')
  );
}

function isInactiveWorker(profile: AuthProfile) {
  return profile.role === 'worker' && profile.status === 'inactive';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const resetAuthState = useCallback(() => {
    setSession(null);
    setUser(null);
    setProfile(null);
    clearCachedProfile();
  }, []);

  const clearInvalidSession = useCallback(
    async (message: string) => {
      const supabase = getSupabaseBrowserClient();
      resetAuthState();
      setAuthError(message);
      try {
        await withTimeout(
          supabase.auth.signOut(),
          'Signing out an invalid session took too long. Please refresh the page.'
        );
      } catch {
        // The local auth state is already cleared, so a failing remote sign-out
        // should not trap the UI in a loading state.
      }
    },
    [resetAuthState]
  );

  const loadProfile = useCallback(async (currentUser: User | null) => {
    if (!currentUser) {
      setProfile(null);
      setAuthError(null);
      return;
    }

    const nextProfile = await fetchProfile(currentUser.id);
    if (isInactiveWorker(nextProfile)) {
      throw new Error(INACTIVE_WORKER_MESSAGE);
    }
    setProfile(nextProfile);
    writeCachedProfile(nextProfile);
    setAuthError(null);
  }, []);

  useEffect(() => {
    const supabase = getSupabaseBrowserClient();

    supabase.auth
      .getSession()
      .then(async ({ data }) => {
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);

        if (data.session?.user) {
          const cachedProfile = readCachedProfile(data.session.user.id);
          if (cachedProfile) {
            setProfile(cachedProfile);
            setAuthError(null);
            setLoading(false);
          }

          try {
            await withTimeout(
              loadProfile(data.session.user),
              'Loading your account took too long. Please sign in again.'
            );
          } catch (error) {
            if (error instanceof Error && error.message === INACTIVE_WORKER_MESSAGE) {
              await clearInvalidSession(INACTIVE_WORKER_MESSAGE);
            } else if (shouldClearSession(error)) {
              await clearInvalidSession(
                'Your saved session was no longer valid. Please sign in again.'
              );
            } else {
              if (!cachedProfile) {
                setProfile(null);
              }
              setAuthError('We could not refresh your account right now. Please try again.');
            }
          }
        }
      })
      .catch(() => {
        resetAuthState();
        setAuthError('We could not restore your session. Please sign in again.');
      })
      .finally(() => setLoading(false));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (nextSession?.user) {
        const cachedProfile = readCachedProfile(nextSession.user.id);
        if (cachedProfile) {
          setProfile(cachedProfile);
          setAuthError(null);
          setLoading(false);
        }

        try {
          await withTimeout(
            loadProfile(nextSession.user),
            'Loading your account took too long. Please sign in again.'
          );
        } catch (error) {
          if (error instanceof Error && error.message === INACTIVE_WORKER_MESSAGE) {
            await clearInvalidSession(INACTIVE_WORKER_MESSAGE);
          } else if (shouldClearSession(error)) {
            await clearInvalidSession(
              'Your saved session was no longer valid. Please sign in again.'
            );
          } else {
            if (!cachedProfile) {
              setProfile(null);
            }
            setAuthError('We could not refresh your account right now. Please try again.');
          }
        }
      } else {
        setProfile(null);
        setAuthError(null);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [clearInvalidSession, loadProfile, resetAuthState]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        'Sign-in timed out. Please try again.'
      );
      if (error) throw error;

      const signedInUser = data.user;
      if (!signedInUser) {
        throw new Error('Signed in, but no user was returned.');
      }

      const nextProfile = await withTimeout(
        fetchProfile(signedInUser.id),
        'Profile lookup timed out after sign-in.'
      );
      if (isInactiveWorker(nextProfile)) {
        resetAuthState();
        await supabase.auth.signOut();
        throw new Error(INACTIVE_WORKER_MESSAGE);
      }

      setSession(data.session ?? null);
      setUser(signedInUser);
      setProfile(nextProfile);
      writeCachedProfile(nextProfile);
      setAuthError(null);

      return nextProfile;
    },
    [resetAuthState]
  );

  const signOut = useCallback(async () => {
    const supabase = getSupabaseBrowserClient();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    resetAuthState();
    setAuthError(null);
  }, [resetAuthState]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    try {
      const nextProfile = await fetchProfile(user.id);
      if (isInactiveWorker(nextProfile)) {
        await clearInvalidSession(INACTIVE_WORKER_MESSAGE);
        return;
      }
      setProfile(nextProfile);
      writeCachedProfile(nextProfile);
      setAuthError(null);
    } catch (error) {
      if (shouldClearSession(error)) {
        await clearInvalidSession(
          'Your session expired or your profile changed. Please sign in again.'
        );
      } else {
        setAuthError('We could not refresh your account right now. Please try again.');
      }
    }
  }, [clearInvalidSession, user]);

  const getAccessToken = useCallback(async () => {
    const supabase = getSupabaseBrowserClient();
    let activeSession = session;

    if (!activeSession) {
      const { data } = await supabase.auth.getSession();
      activeSession = data.session ?? null;
    }

    if (!activeSession?.access_token) {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session) {
        activeSession = data.session;
        setSession(data.session);
        setUser(data.session.user);
      }
    }

    return activeSession?.access_token ?? null;
  }, [session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      profile,
      loading,
      authError,
      signIn,
      signOut,
      refreshProfile,
      getAccessToken,
    }),
    [authError, getAccessToken, loading, profile, refreshProfile, session, signIn, signOut, user]
  );

  return <AuthContext.Provider data-component-id="src\contexts\AuthContext.tsx:327:9" data-component-path="src\contexts\AuthContext.tsx" data-component-line="327" data-component-end-line="327" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider.');
  }
  return context;
}
